// VendorAppHost.js
// Owns every vendor iframe for the session (docs/vendor-apps-contract.md §5).
// Mounted once in layouts/User.js OUTSIDE <Routes>, so navigating away from
// the Apps tab cannot unmount a frame — subscriptions and buffers live inside
// the vendor's page, and an unmount would silently end both.
//
// Frames are created lazily on first open and then kept alive (hidden, not
// unmounted) until logout or the app is disabled. The active frame is
// positioned over #vendor-app-viewport (rendered by the /apps view); all
// others are parked off-screen but running.
import { useContext, useEffect, useRef } from 'react';
import React from 'react';
import { toast } from 'react-toastify';
import { GlobalContext } from '../../api/GlobalContext';
import vendorStore, { isTpiApp } from '../../utils/vendorApps/store';
import { createBridge } from '../../utils/vendorApps/bridge';
import { createEvaluator } from '../../utils/vendorApps/evaluator';

export const VIEWPORT_ID = 'vendor-app-viewport';
// allow-modals: vendor pages confirm destructive actions with confirm() (RxCart's
// remove-from-cart trash can). Without it Chrome silently answers false, so the
// action never happens. The sandbox reaches every frame nested inside.
const SANDBOX = 'allow-scripts allow-same-origin allow-forms allow-popups allow-downloads allow-modals';

// Memory is bounded structurally, not measured: a cross-origin frame's heap is
// invisible to us (performance.memory only sees our own page, and
// measureUserAgentSpecificMemory needs cross-origin isolation we don't have).
// So: at most MAX_ALIVE_FRAMES vendor pages exist at once (least-recently
// viewed is torn down), and a frame nobody has looked at for IDLE_UNLOAD_MS is
// torn down too. Buffers and links live in OUR store and survive; the vendor
// page simply reloads and re-subscribes the next time it is opened.
export const MAX_ALIVE_FRAMES = 5; // used apps eager-mount up to this many; least-recently-viewed beyond it are torn down
export const IDLE_UNLOAD_MS = 30 * 60 * 1000;
const IDLE_SWEEP_MS = 60 * 1000;

const PROD_ORIGIN = 'https://app.comprehendpt.com';

function launchSrc(app) {
  const url = new URL(app.launchUrl);
  url.searchParams.set('comprehend', '1');
  // Off production (dev.comprehendpt.com, localhost Dev Console) the vendor
  // script must pin us instead of prod. comprehend.js honours only those two.
  if (window.location.origin !== PROD_ORIGIN) url.searchParams.set('comprehend_host', window.location.origin);
  return url.toString();
}

const HIDDEN = { position: 'fixed', left: '-10000px', top: 0, width: '1px', height: '1px', visibility: 'hidden', pointerEvents: 'none', border: 0 };

export default function VendorAppHost({ store = vendorStore }) {
  const { apiClient } = useContext(GlobalContext);
  const containerRef = useRef(null);
  const framesRef = useRef(new Map()); // appId → { iframe, bridge }
  // A clinician action (Comprehend / refresh) first re-announces the patient
  // (reason 'refresh'); apps answer that by re-subscribing. So evaluation is
  // scheduled a beat later and pushed back by each subscribe that arrives in
  // the window — one evaluation, against the subscriptions the app actually has.
  const pendingEvalRef = useRef(new Map()); // appId → { reason, until, timer }
  const scheduleEval = (appId, reason, delay = 350) => {
    const prev = pendingEvalRef.current.get(appId);
    if (prev) clearTimeout(prev.timer);
    const until = prev?.until || Date.now() + 2500;
    store._setStatus(appId, { evaluating: true });
    const timer = setTimeout(() => {
      pendingEvalRef.current.delete(appId);
      Promise.resolve(evaluateApp(appId, reason)).finally(() => store._setStatus(appId, { evaluating: false }));
    }, delay);
    pendingEvalRef.current.set(appId, { reason, until, timer });
  };
  const evaluatorRef = useRef(null);
  const providerLoadedRef = useRef(false);

  // C3: links a vendor establishes are persisted on the patient record.
  // (Buffers reach the note via the visit's app_context field — the buffer/answers
  // listeners in ConversationDetailsCX/Native.)
  useEffect(() => {
    if (!apiClient) return undefined;
    store.persistRef = (patientId, appId, patientRef) =>
      typeof apiClient.setPatientVendorRef === 'function'
        ? apiClient.setPatientVendorRef(patientId, appId, patientRef)
        : Promise.resolve();
    // Provider-level content (about the clinician) lives on the user record,
    // where chat reads it.
    store.persistProviderContext = (appId, markdown) =>
      apiClient.vendorAppsSupported === true && typeof apiClient.setProviderContext === 'function'
        ? apiClient.setProviderContext(appId, markdown)
        : Promise.resolve();
    return () => {
      store.persistRef = null;
      store.persistProviderContext = null;
    };
  }, [store, apiClient]);

  // C1: registered apps arrive with the profile; keep the store in sync
  // (the Dev Console's localStorage override still layers on top).
  useEffect(() => {
    if (!apiClient) return undefined;
    // Only once the profile has actually delivered a list — before that,
    // leave whatever the store already holds (tests, Dev Console) alone.
    const sync = (client) => {
      if (Array.isArray(client?.vendorApps)) store.loadApps(client.vendorApps);
      // Profile in → backend switched → the list can be fetched from the right server.
      store.ensureApps(client, { force: true }).then(() => {
        armMountTick();
        // What apps said about the clinician in earlier sessions, for App data.
        if (!providerLoadedRef.current && client?.vendorAppsSupported === true && typeof client.getProviderContext === 'function') {
          providerLoadedRef.current = true;
          Promise.resolve(client.getProviderContext())
            .then((rsp) => store.hydrateProviderContext(rsp?.providerContext))
            .catch(() => { providerLoadedRef.current = false; });
        }
      });
    };
    sync(apiClient);
    if (apiClient.onProfileUpdated) apiClient.onProfileUpdated(sync);
    return () => {
      if (apiClient.offProfileUpdated) apiClient.offProfileUpdated(sync);
    };
  }, [store, apiClient]);

  // One evaluator; mode follows the store (Dev Console flips it).
  useEffect(() => {
    const build = () =>
      (evaluatorRef.current = createEvaluator({
        store,
        apiClient,
        mode: store.evalMode,
        mockScenario: () => store.mockScenario,
      }));
    build();
    return store.on('evalMode', build);
  }, [store, apiClient]);

  const evaluateApp = (appId, reason, onlySubId = null) => {
    const entry = framesRef.current.get(appId);
    if (!entry || !evaluatorRef.current) return;
    return evaluatorRef.current.evaluate(appId, {
      reason,
      onlySubId,
      deliver: (subId, payload) => {
        entry.bridge.sendAnswers(subId, payload);
        // Also publish for persistence (app_context.answers, by structure hash) and the note-tab rows.
        const sub = store.subsFor(appId).get(subId);
        if (sub) store._changed('answers', { appId, subId, hash: sub.hash, payload });
      },
      fail: (subId, code, message) => entry.bridge.sendError(subId, code, message),
    });
  };

  // Eager mount: apps this provider has opened before load hidden as soon as a
  // visit has a patient, so they have set context and subscribed by the time the
  // clinician presses Comprehend — whether or not the Apps tab is ever shown.
  const mountUsedApps = () => {
    if (!store.patient) return;
    // An enabled TPI connector must match on assignment before its first open.
    Array.from(new Set([store.apps.find(isTpiApp)?.id, ...store.usedAppIds()])).filter(id => id).filter(id => store.getApp(id)?.enabled).slice(0, MAX_ALIVE_FRAMES).forEach((id) => {
      if (!framesRef.current.has(id)) ensureFrame(id);
    });
  };
  // True while a used app still has no frame — e.g. the app list has not arrived
  // yet on a hard reload — so the convergence tick below knows to keep going.
  const eagerMountPending = () =>
    Boolean(store.patient) && store.usedAppIds().slice(0, MAX_ALIVE_FRAMES).some((id) => !framesRef.current.has(id));
  // Mount order on a hard reload (host, patient, app list) is not fixed. Rather
  // than a tick for the whole session, arm a short one whenever something that
  // could complete the eager mount happens, and let it stop itself as soon as
  // nothing is pending (or after 30 s). With zero apps it never runs at all.
  const mountTickRef = useRef({ timer: null, ticks: 0 });
  const armMountTick = () => {
    mountUsedApps();
    const t = mountTickRef.current;
    if (t.timer || !eagerMountPending()) return;
    t.ticks = 0;
    t.timer = setInterval(() => {
      mountUsedApps();
      if (!eagerMountPending() || ++t.ticks >= 20) {
        clearInterval(t.timer);
        t.timer = null;
      }
    }, 1500);
  };

  const destroyFrame = (appId) => {
    const entry = framesRef.current.get(appId);
    if (!entry) return;
    entry.bridge.destroy();
    entry.iframe.remove();
    framesRef.current.delete(appId);
    store.clearApp(appId);
  };

  const touch = (appId) => {
    const entry = framesRef.current.get(appId);
    if (entry) entry.lastActiveAt = Date.now();
  };

  // Tear down the least-recently-viewed frames until we're under the cap.
  const enforceCap = (keepAppId) => {
    while (framesRef.current.size > MAX_ALIVE_FRAMES) {
      let victim = null;
      framesRef.current.forEach((entry, appId) => {
        if (appId === keepAppId || appId === store.activeAppId) return;
        if (!victim || entry.lastActiveAt < victim.lastActiveAt) victim = { appId, lastActiveAt: entry.lastActiveAt };
      });
      if (!victim) return;
      destroyFrame(victim.appId);
    }
  };

  const ensureFrame = (appId) => {
    if (framesRef.current.has(appId)) {
      touch(appId);
      return framesRef.current.get(appId);
    }
    const app = store.getApp(appId);
    if (!app || !app.enabled || !containerRef.current) return null;
    if (app.kind !== 'iframe') return null; // adapters provide from the extension, nothing to frame
    const iframe = document.createElement('iframe');
    iframe.title = app.name;
    iframe.setAttribute('sandbox', SANDBOX);
    iframe.setAttribute('allow', 'clipboard-write');
    iframe.setAttribute('referrerpolicy', 'origin');
    // The frame is sized to the app's reported content height, so it must never grow
    // scrollbars of its own: a page that overflows sideways at 350 px would otherwise
    // get a horizontal bar, which eats ~15 px of height and summons a vertical bar too
    // (the "double scroll"). Apps are told to fit their width (350 px design-first).
    iframe.setAttribute('scrolling', 'no');
    Object.assign(iframe.style, HIDDEN);
    const bridge = createBridge({
      app,
      iframe,
      store,
      onDataRequest: (operation, value, expectedRevision) => {
        const installed = store.getApp(appId);
        if (!installed?.enabled || installed.embedOrigin !== app.embedOrigin) return { error: 'APP_DATA_FORBIDDEN' };
        if (operation === 'get') return apiClient.getVendorAppData(app.id, app.embedOrigin);
        return apiClient.saveVendorAppData(app.id, app.embedOrigin, value, expectedRevision);
      },
      onEvaluate: (subId) => evaluateApp(appId, 'manual', subId),
      // Subscribing does NOT answer on its own. It only re-arms an evaluation the
      // clinician already asked for (see scheduleEval) so a re-subscribe made in
      // response to patient(refresh) is what gets answered.
      onSubscribe: () => {
        const p = pendingEvalRef.current.get(appId);
        if (p && Date.now() < p.until) scheduleEval(appId, p.reason);
      },
    });
    // patient(ready) is sent when comprehend.js says hello (bridge.js), not on
    // the iframe's load event: a script injected after load would miss it.
    // The page itself has loaded (whether or not it runs comprehend.js — a
    // login page doesn't): the app-open splash may lift (AppSplash).
    iframe.addEventListener('load', () => store._setStatus(appId, { frameLoadedAt: Date.now() }));
    iframe.src = launchSrc(app);
    containerRef.current.appendChild(iframe);
    const entry = { iframe, bridge, app, lastActiveAt: Date.now() };
    framesRef.current.set(appId, entry);
    enforceCap(appId);
    startPlacing();
    return entry;
  };

  // Idle sweep: unload frames nobody has looked at for a while.
  useEffect(() => {
    const timer = setInterval(() => {
      const cutoff = Date.now() - IDLE_UNLOAD_MS;
      Array.from(framesRef.current.entries()).forEach(([appId, entry]) => {
        if (appId !== store.activeAppId && entry.lastActiveAt < cutoff) destroyFrame(appId);
      });
    }, IDLE_SWEEP_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  // Position the active frame over the viewport; park the rest. The loop runs
  // per animation frame only while at least one frame exists — it stops itself
  // when the last frame is destroyed and ensureFrame starts it again.
  const placeRafRef = useRef(0);
  const place = () => {
    if (framesRef.current.size === 0) {
      placeRafRef.current = 0;
      return;
    }
    const viewport = document.getElementById(VIEWPORT_ID);
    const rect = viewport?.getBoundingClientRect();
    framesRef.current.forEach((entry, appId) => {
      const active = appId === store.activeAppId && rect && rect.width > 0;
      entry.bridge.sendVisibility(Boolean(active && document.visibilityState !== 'hidden'));
      if (active) {
        // Clamp to what is actually on screen: in the extension side panel the tab
        // content can be wider than the panel, and a frame sized to it would render
        // its layout for a width the clinician can't see (right half cut off).
        const visibleWidth = Math.max(240, Math.min(rect.width, window.innerWidth - rect.left - 4));
        Object.assign(entry.iframe.style, {
          position: 'fixed',
          left: `${rect.left}px`,
          top: `${rect.top}px`,
          width: `${visibleWidth}px`,
          height: `${rect.height}px`,
          visibility: 'visible',
          pointerEvents: 'auto',
          border: 0,
          zIndex: 5,
          background: '#fff',
        });
      } else {
        Object.assign(entry.iframe.style, HIDDEN);
      }
    });
    placeRafRef.current = requestAnimationFrame(place);
  };
  const startPlacing = () => {
    if (!placeRafRef.current) placeRafRef.current = requestAnimationFrame(place);
  };
  useEffect(() => {
    const update = () => {
      const rect = document.getElementById(VIEWPORT_ID)?.getBoundingClientRect();
      framesRef.current.forEach((entry, id) => entry.bridge.sendVisibility(Boolean(
        document.visibilityState !== 'hidden' && id === store.activeAppId && rect?.width > 0,
      )));
    };
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, [store]);
  useEffect(
    () => () => {
      cancelAnimationFrame(placeRafRef.current);
      placeRafRef.current = 0;
    },
    [],
  );

  // Store events → frames.
  useEffect(() => {
    const offs = [
      store.on('activeApp', ({ appId }) => {
        if (appId) {
          ensureFrame(appId);
          touch(appId);
        }
      }),
      store.on('patient', () => {
        pendingEvalRef.current.forEach(p => clearTimeout(p.timer));
        pendingEvalRef.current.clear();
        framesRef.current.forEach((entry, appId) => {
          entry.bridge.sendPatient('changed');
          // chart open re-announces the patient only; no evaluation until the clinician acts
        });
        armMountTick();
      }),
      store.on('apps', () => armMountTick()),
      store.on('zoom', ({ appId, zoom }) => framesRef.current.get(appId)?.bridge.sendZoom(zoom)),
      // Clinician action (note generate, Refresh apps): tell every app to send
      // its latest context — the `patient` event with reason 'refresh' — then
      // evaluate subscriptions.
      store.on('evaluate', ({ reason, appId }) => {
        const targets = appId ? [appId] : Array.from(framesRef.current.keys());
        targets.forEach((id) => {
          if (!framesRef.current.get(id)) return;
          framesRef.current.get(id).bridge.sendPatient('refresh');
          scheduleEval(id, reason);
        });
      }),
      // The vendor only hears CONTEXT_REJECTED; the clinician is the one who can fix it.
      store.on('mismatch', ({ appId, theirs, ours }) => {
        const app = store.getApp(appId);
        const id = `vendor-mismatch-${appId}`;
        // The clinician decides, in our UI: confirm the link once and the vendor's id is authoritative from then on.
        toast.warn(
          <div>
            <div>
              {app?.name || 'An app'} has <strong>{theirs}</strong> open; this visit is <strong>{ours}</strong>. Not linked.
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-dark mt-2"
              onClick={() => {
                store.confirmLink(appId);
                toast.dismiss(id);
              }}
            >
              Yes, same patient — link them
            </button>
          </div>,
          { toastId: id, autoClose: 12000 },
        );
      }),
      // A context sent while the visit has no patient is surfaced ONLY in the Apps
      // tray (VendorAppsPanel: status line + "Use <app>'s patient"), not as a toast.
      store.on('unlinked', ({ appId }) => {
        framesRef.current.get(appId)?.bridge.sendPatient('changed'); // handle now arrives without yourId
      }),
      store.on('linked', ({ appId }) => {
        // The app now gets a handle with yourId set and re-syncs (context + subscriptions).
        framesRef.current.get(appId)?.bridge.sendPatient('changed');
        toast.dismiss(`vendor-mismatch-${appId}`);
      }),
      store.on('apps', ({ apps }) => {
        framesRef.current.forEach((entry, appId) => {
          const now = apps.find((a) => a.id === appId);
          if (!now || !now.enabled || now.launchUrl !== entry.app.launchUrl || now.embedOrigin !== entry.app.embedOrigin) destroyFrame(appId);
        });
      }),
    ];
    if (store.activeAppId) ensureFrame(store.activeAppId);
    armMountTick();
    return () => {
      clearInterval(mountTickRef.current.timer);
      mountTickRef.current.timer = null;
      offs.forEach((off) => off());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  // Logout / layout unmount: tear everything down.
  useEffect(() => () => {
    pendingEvalRef.current.forEach(p => clearTimeout(p.timer));
    pendingEvalRef.current.clear();
    Array.from(framesRef.current.keys()).forEach(destroyFrame);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <div ref={containerRef} data-vendor-app-frames="" />;
}
